import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { Footer } from '../../../common/footer';
import { HomeNavbar } from '../../../common/home-navbar';
import { InformationMenu } from '../information-menu';
import { ScreenStore } from '../../../stores/ScreenStore';
import { constants } from '../../../config';
import { routeNaming, AppLink } from '../../../routes';
import { RecaptchaComponent } from '../../../common/recaptcha-component';
import { BasicInput } from '../../../common/basic-input';
import { SecondaryButton } from '../../../common/secondary-button';
import { SimpleButton } from '../../../common/simple-button';
import { ServiceTrackingStore } from './stores/ServiceTrackingStore';
import { Spinner } from '../../../common/spinner';
import { QUESTIONS } from '../../../models/InfoAndQuestions';
import styles from './ServiceTracking.css';
import { ERROR_CODES } from '../../../models/ApiError';
import { Modal } from '../../../common/modal';
import { InformationAndQuestions } from '../../../common/information-and-questions';
import { ParamsHelper } from '../../../helpers/ParamsHelper';
import { SEO } from '../../../common/seo';

const SCREENS = {
  SHOW_STATE: 'showState',
  FORM: 'form',
};

const questionsOptions = {
  acceptQuestion: {
    message: 'Acepto el servicio',
  },
  cancelQuestion: {
    message: 'No acepto el servicio',
  },
  oneWeekQuestion: {
    message: 'Retiro el producto en 1 semana',
  },
  regretQuestion: {
    message: 'Me arrepentí, quiero aceptar el servicio',
  },
  disposalQuestion: {
    message: 'No voy a retirar el producto. Renuncio a él',
  },
};

const IPropTypes = {
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }).isRequired,
};

class ServiceTracking extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      actualScreen: SCREENS.FORM,

      serviceId: null,
      personalIdOrRut: null,

      recaptchaError: null,
      recaptchaToken: null,
    };

    this._updateServiceId = this._updateServiceId.bind(this);
    this._updatePersonalIdOrRut = this._updatePersonalIdOrRut.bind(this);

    this._onRecaptchaError = this._onRecaptchaError.bind(this);
    this._onRecaptchaExpiry = this._onRecaptchaExpiry.bind(this);
    this._onRecaptchaSuccess = this._onRecaptchaSuccess.bind(this);

    this._submitData = this._submitData.bind(this);
    this._updateService = this._updateService.bind(this);
    this._loadCustomerInfo = this._loadCustomerInfo.bind(this);

    this._toggleCancel = this._toggleCancel.bind(this);

    this._onCloseModal = this._onCloseModal.bind(this);
  }

  componentDidMount() {
    this._loadCustomerInfo();
  }

  componentWillUnmount() {
    ServiceTrackingStore.clearErrors();
  }

  async _loadCustomerInfo() {
    const { t } = ParamsHelper.parseQueryParams(this.props.location.search);
    if (t) {
      await ServiceTrackingStore.findCustomerData({ token: t });
      this.setState({
        serviceId: ServiceTrackingStore.serviceTracking &&
        ServiceTrackingStore.serviceTracking.serviceId,
        personalIdOrRut: ServiceTrackingStore.serviceTracking &&
        ServiceTrackingStore.serviceTracking.cardId,
      });
    }
  }

  _updateServiceId(event) {
    const { target: { value } } = event;
    this.setState({
      serviceId: value,
    });
  }

  _updatePersonalIdOrRut(event) {
    const { target: { value } } = event;
    this.setState({
      personalIdOrRut: value,
    });
  }

  _onRecaptchaSuccess(token) {
    this.setState({
      recaptchaError: false,
      recaptchaToken: token,
    });
  }

  _onRecaptchaError() {
    this.setState({
      recaptchaError: true,
    });
  }

  _onRecaptchaExpiry() {
    this.setState({
      recaptchaToken: null,
    });
  }

  _dataIsValid() {
    const dataIsValid = (
      !!this.state.personalIdOrRut &&
      !!this.state.serviceId &&
      !!this.state.recaptchaToken
    );
    return dataIsValid;
  }

  async _submitData(event) {
    event.preventDefault();
    if (!this._dataIsValid()) {
      return;
    }

    await ServiceTrackingStore.findServiceTracking({
      serviceId: this.state.serviceId,
      personalIdOrRut: this.state.personalIdOrRut,
      recaptchaToken: this.state.recaptchaToken,
    });

    if (!ServiceTrackingStore.findError &&
        !ServiceTrackingStore.findApiError &&
        !ServiceTrackingStore.findInternalError) {
      this.setState({
        actualScreen: SCREENS.SHOW_STATE,
      });
    }
  }

  async _updateService(event, question, accept) {
    event.preventDefault();

    let dispose = null;
    if (question === QUESTIONS.disposalQuestion) {
      dispose = 1;
    } else if (question === QUESTIONS.cancelQuestion) {
      dispose = 0;
    } else if (question === QUESTIONS.oneWeekQuestion) {
      dispose = 2;
    }

    await ServiceTrackingStore.updateServiceTracking({
      serviceId: this.state.serviceId,
      personalIdOrRut: this.state.personalIdOrRut,
      accept,
      dispose,
    });
  }

  _onCloseModal() {
    this.setState({
      actualScreen: SCREENS.FORM,
    });
  }

  _toggleCancel(event) {
    event.preventDefault();
    this.setState({
      actualScreen: SCREENS.FORM,
      serviceId: null,
      personalIdOrRut: null,

      recaptchaError: null,
      recaptchaToken: null,
    });
  }

  static renderInternalError() {
    return (
      <p className={styles.errorMessage}>
        Estamos experimentando algunas dificultades en nuestro sitio.
        Por favor intente nuevamente en unos momentos.
      </p>
    );
  }

  static renderError() {
    return (
      <p className={styles.errorMessage}>
        Ha ocurrido un error, por favor intente nuevamente.
      </p>
    );
  }

  static renderInvalidActionError() {
    return (
      <p className={styles.errorMessage}>
        El estado del servicio no permite realizar esta acción.
      </p>
    );
  }

  render() {
    const isFormScreen = this.state.actualScreen === SCREENS.FORM;
    const isShowStateScreen = this.state.actualScreen === SCREENS.SHOW_STATE;

    const dataLoaded = ServiceTrackingStore.serviceTracking !== null;

    const serviceIdError = this.state.serviceId === '' ? 'Este campo no puede ser vacío' : null;
    const personalIdOrRutError = this.state.personalIdOrRut === '' ? 'Este campo no puede ser vacío' : null;

    const showMobileVersion = ScreenStore.screenWidth <= constants.breakpoints.mobileThreshold;

    const serviceTrackingError = dataLoaded ? ServiceTrackingStore.serviceTracking.error : null;

    return (
      <div className={styles.container}>
        <SEO
          title="Seguimiento de servicios"
          description="Accede a toda la información relevante sobre Carlos Gutiérrez S.A, nuestros servicios, políticas y cómo podemos ayudarte. Encuentra detalles sobre nuestros productos, condiciones de uso, privacidad, envíos y más. Estamos comprometidos a ofrecerte la mejor experiencia, brindándote transparencia y claridad en todos nuestros procesos. Si tienes preguntas o dudas, esta sección es tu recurso principal para obtener respuestas. Descubre todo lo que necesitas saber y disfruta de una experiencia de compra segura y satisfactoria con nosotros"
          type="website"
        />
        <HomeNavbar />
        <div className={styles.mainWrapper}>
          <div className={styles.mainContent}>
            {showMobileVersion ?
              <p className={styles.breadcrumb}>
                <AppLink
                  className={styles.breadcrumb}
                  routeName={routeNaming.INFORMATION_MENU_MOBILE}
                >
                  Información
                </AppLink>
                <span> &gt; Seguimiento de servicios</span>
              </p>
              : <InformationMenu indexSelectedItem={5} />}
            <div className={styles.bodyContainer}>
              <div className={styles.titleContainer}>
                <h1 className={styles.mainTitle}> Seguimiento de servicios </h1>
                {
                  isShowStateScreen &&
                  <button className={styles.cancelButton} onClick={this._toggleCancel}>
                    VOLVER
                  </button>
                }
              </div>
              <div className={styles.contentSection} >
                {
                  isFormScreen &&
                  <form
                    className={styles.serviceTrackingForm}
                    onSubmit={this._submitData}
                  >
                    <BasicInput
                      className={styles.input}
                      label="Número de servicio"
                      name="serviceId"
                      onChange={this._updateServiceId}
                      error={serviceIdError}
                      value={this.state.serviceId || ''}
                      disabled={ServiceTrackingStore.loadingServiceTracking}
                    />
                    <BasicInput
                      className={styles.input}
                      label="Cédula o RUT"
                      name="personalIdOrRut"
                      onChange={this._updatePersonalIdOrRut}
                      error={personalIdOrRutError}
                      value={this.state.personalIdOrRut || ''}
                      disabled={ServiceTrackingStore.loadingServiceTracking}
                    />
                    <div className={styles.submitSection}>
                      <div className={styles.recaptcha}>
                        {
                          !!this.state.recaptchaError &&
                          <p className={styles.errorMessage}>
                            Ha ocurrido un error, intente realizar el reCAPTCHA de nuevo.
                          </p>
                        }
                        <RecaptchaComponent
                          onError={this._onRecaptchaError}
                          onExpiry={this._onRecaptchaExpiry}
                          onSuccess={this._onRecaptchaSuccess}
                        />
                      </div>
                      {
                        ServiceTrackingStore.findInternalError &&
                        ServiceTracking.renderInternalError()
                      }
                      {
                        ServiceTrackingStore.findError &&
                        ServiceTracking.renderError()
                      }
                      {
                        ServiceTrackingStore.findApiError &&
                        <p className={styles.errorMessage}>
                          Servicio y/o identificador inválido.
                        </p>
                      }
                      <SecondaryButton
                        className={styles.submitButton}
                        type="submit"
                        disabled={!this._dataIsValid()}
                      >
                        Ver Estado
                      </SecondaryButton>
                    </div>
                  </form>
                }
                {
                  isShowStateScreen &&
                  !dataLoaded &&
                  <Spinner className={styles.spinner} />
                }
                {
                  isShowStateScreen && dataLoaded &&
                  <InformationAndQuestions
                    sections={ServiceTrackingStore.serviceTracking.sections || []}
                    options={questionsOptions}
                    onActionQuestion={this._updateService}
                  >
                    {
                      ServiceTrackingStore.updateError &&
                      ServiceTracking.renderError()
                    }
                    {
                      ServiceTrackingStore.updateInternalError &&
                      ServiceTracking.renderInternalError()
                    }
                    {
                      ServiceTrackingStore.updateApiError === ERROR_CODES.INVALID_SERVICE_ACTION &&
                      ServiceTracking.renderInvalidActionError()
                    }
                  </InformationAndQuestions>
                }
                {
                  isShowStateScreen &&
                  dataLoaded &&
                  !!serviceTrackingError &&
                  <Modal>
                    <div className={styles.modalContainer}>
                      <p className={styles.serviceTrackingError}>{serviceTrackingError}</p>
                      <SimpleButton
                        className={styles.modalButton}
                        onClick={this._onCloseModal}
                      >
                        Cerrar
                      </SimpleButton>
                    </div>
                  </Modal>
                }
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
}

ServiceTracking.propTypes = IPropTypes;

const ObserveredServiceTracking = observer(ServiceTracking);

export { ObserveredServiceTracking as ServiceTracking };



// WEBPACK FOOTER //
// ./src/pages/information/service-tracking/ServiceTracking.jsx